import { FilterMenu, type FilterOption } from '@/components/FilterMenu'
import { InputFilterMenu } from '@/components/FilterMenu/InputFilterMenu'
import { OrgFilterMenu } from '@/components/OrgFilterMenu/OrgFilterMenu'
import { OrgFilterTagValue } from '@/components/OrgFilterMenu/OrgFilterTagValue'
import type { DateRangePreset } from '@/features/AuditLogs/hooks/useAuditLogFilters'
import {
  AUDIT_LOG_STATUSES,
  type AuditLogStatus,
} from '@/features/AuditLogs/lib/auditSourceParams'
import { Button, Flex, HStack, Icon, Tag } from '@chakra-ui/react'
import { X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AffectedUserFilter } from './AffectedUserFilter'

interface AuditLogsFiltersProps {
  dateRangePreset: DateRangePreset
  hiveId: string | null
  orgId: string | null
  user: string | null
  status: AuditLogStatus | null
  licenseId: string | null
  userId: string | null
  hasActiveFilters: boolean
  onDateRangeChange: (preset: DateRangePreset) => void
  onHiveIdChange: (value: string | null) => void
  onOrgIdChange: (value: string | null) => void
  onUserChange: (value: string | null) => void
  onStatusChange: (value: AuditLogStatus | null) => void
  onLicenseIdChange: (value: string | null) => void
  onUserIdChange: (value: string | null) => void
  onClearFilters: () => void
}

export function AuditLogsFilters({
  dateRangePreset,
  hiveId,
  orgId,
  user,
  status,
  licenseId,
  userId,
  hasActiveFilters,
  onDateRangeChange,
  onHiveIdChange,
  onOrgIdChange,
  onUserChange,
  onStatusChange,
  onLicenseIdChange,
  onUserIdChange,
  onClearFilters,
}: Readonly<AuditLogsFiltersProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const { t: tTranslation } = useTranslation('translation', {
    useSuspense: false,
  })
  const [lookedUpEmail, setLookedUpEmail] = useState<{
    userId: string
    email: string
  } | null>(null)

  const dateRangeOptions: FilterOption[] = useMemo(
    () => [
      {
        value: 'lastHour',
        label: String(t('auditLogs.filters.presets.lastHour')),
      },
      {
        value: 'last24Hours',
        label: String(t('auditLogs.filters.presets.last24Hours')),
      },
      {
        value: 'last7Days',
        label: String(t('auditLogs.filters.presets.last7Days')),
      },
      {
        value: 'last30Days',
        label: String(t('auditLogs.filters.presets.last30Days')),
      },
      {
        value: 'allTime',
        label: String(t('auditLogs.filters.presets.allTime')),
      },
    ],
    [t],
  )

  const statusOptions: FilterOption[] = useMemo(
    () =>
      AUDIT_LOG_STATUSES.map((value) => ({
        value,
        label: String(t(`auditLogs.status.${value}`)),
      })),
    [t],
  )

  const selectedDateRange = dateRangeOptions.find(
    (opt) => opt.value === dateRangePreset,
  )
  const selectedStatus = status
    ? statusOptions.find((opt) => opt.value === status)
    : null

  const dateRangeLabel = String(t('auditLogs.filters.dateRange'))
  const hiveIdLabel = String(t('auditLogs.filters.hiveId'))
  const orgIdLabel = String(t('auditLogs.filters.orgId'))
  const userLabel = String(t('auditLogs.filters.user'))
  const statusLabel = String(t('auditLogs.filters.status'))
  const licenseIdLabel = String(t('auditLogs.filters.licenseId'))
  const affectedUserLabel = String(t('auditLogs.filters.affectedUser.label'))
  const userIdTagText =
    lookedUpEmail !== null && lookedUpEmail.userId === userId
      ? lookedUpEmail.email
      : userId

  return (
    <Flex direction="column" gap={3}>
      <HStack gap={2} wrap="wrap">
        <FilterMenu
          buttonLabel={dateRangeLabel}
          options={dateRangeOptions}
          selectedOption={selectedDateRange ?? null}
          onOptionChange={(option) =>
            onDateRangeChange(
              (option?.value as DateRangePreset) ?? 'last24Hours',
            )
          }
        />

        <InputFilterMenu
          buttonLabel={hiveIdLabel}
          placeholder={hiveIdLabel}
          selectedValue={hiveId}
          onSearch={(value) => onHiveIdChange(value || null)}
        />

        <OrgFilterMenu
          buttonLabel={orgIdLabel}
          selectedOrgId={orgId}
          onApply={onOrgIdChange}
          inputId="audit-logs-org-filter"
        />

        <InputFilterMenu
          buttonLabel={userLabel}
          placeholder={userLabel}
          selectedValue={user}
          onSearch={(value) => onUserChange(value || null)}
        />

        <AffectedUserFilter
          selectedUserId={userId}
          onApply={(id, email) => {
            setLookedUpEmail(email === undefined ? null : { userId: id, email })
            onUserIdChange(id)
          }}
        />

        <FilterMenu
          buttonLabel={statusLabel}
          options={statusOptions}
          selectedOption={selectedStatus ?? null}
          onOptionChange={(option) =>
            onStatusChange((option?.value as AuditLogStatus) ?? null)
          }
        />

        <InputFilterMenu
          buttonLabel={licenseIdLabel}
          placeholder={licenseIdLabel}
          selectedValue={licenseId}
          onSearch={(value) => onLicenseIdChange(value || null)}
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            colorPalette="gray"
          >
            <Icon as={X} />
            {tTranslation('components.filterMenu.clearAllFilters')}
          </Button>
        )}
      </HStack>

      {hasActiveFilters && (
        <HStack gap={2} wrap="wrap">
          {dateRangePreset !== 'last24Hours' && selectedDateRange && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {dateRangeLabel}: {selectedDateRange.label}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-date-range-tag"
                  aria-label={`Remove ${dateRangeLabel} filter`}
                  cursor="pointer"
                  onClick={() => onDateRangeChange('last24Hours')}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {hiveId && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {hiveIdLabel}: {hiveId}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-hive-id-tag"
                  aria-label={`Remove ${hiveIdLabel} filter`}
                  cursor="pointer"
                  onClick={() => onHiveIdChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {orgId && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {orgIdLabel}: <OrgFilterTagValue orgId={orgId} />
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-org-id-tag"
                  aria-label={`Remove ${orgIdLabel} filter`}
                  cursor="pointer"
                  onClick={() => onOrgIdChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {user && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {userLabel}: {user}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-user-tag"
                  aria-label={`Remove ${userLabel} filter`}
                  cursor="pointer"
                  onClick={() => onUserChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {userId && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {affectedUserLabel}: {userIdTagText}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-user-id-tag"
                  aria-label={`Remove ${affectedUserLabel} filter`}
                  cursor="pointer"
                  onClick={() => onUserIdChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {status && selectedStatus && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {statusLabel}: {selectedStatus.label}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-status-tag"
                  aria-label={`Remove ${statusLabel} filter`}
                  cursor="pointer"
                  onClick={() => onStatusChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {licenseId && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {licenseIdLabel}: {licenseId}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-license-id-tag"
                  aria-label={`Remove ${licenseIdLabel} filter`}
                  cursor="pointer"
                  onClick={() => onLicenseIdChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}
        </HStack>
      )}
    </Flex>
  )
}
